import { useGetCmsTheme, useUpdateCmsTheme } from "@/api/cms";
import type { CmsColor, CmsTheme } from "@/lib/cms/cms-theme.constants";
import { isValidHex } from "@/lib/cms/cms-theme.constants";
import type { CmsThemeForm } from "@/lib/cms/cms-types";
import { useCallback } from "react";

function themeToPatchPayload(theme: CmsTheme): CmsThemeForm & { id: string } {
  return {
    id: theme.id,
    colors: theme.colors.map((c, index) => ({
      id: c.id,
      name: c.name,
      hex: c.hex,
      sortOrder: c.sortOrder ?? index,
    })),
    defaultPageBackgroundColorId: theme.defaultPageBackgroundColorId,
    defaultPageTextColorId: theme.defaultPageTextColorId,
    defaultPageAltBackgroundColorId: theme.defaultPageAltBackgroundColorId,
    defaultPageAltTextColorId: theme.defaultPageAltTextColorId,
    defaultPageFontFamily: theme.defaultPageFontFamily,
    defaultPageFontVariant: theme.defaultPageFontVariant,
    header: {
      backgroundColorId: theme.headerConfig.backgroundColorId,
      textColorId: theme.headerConfig.textColorId,
      separatorType: theme.headerConfig.separatorType,
      separatorColorId: theme.headerConfig.separatorColorId,
      buttonColorId: theme.headerConfig.buttonColorId,
      fontFamily: theme.headerConfig.fontFamily,
      fontVariant: theme.headerConfig.fontVariant,
    },
    footer: {
      backgroundColorId: theme.footerConfig.backgroundColorId,
      textColorId: theme.footerConfig.textColorId,
      separatorType: theme.footerConfig.separatorType,
      separatorColorId: theme.footerConfig.separatorColorId,
      fontFamily: theme.footerConfig.fontFamily,
      fontVariant: theme.footerConfig.fontVariant,
    },
  };
}

export function useEnsurePaletteColor() {
  const { data: theme } = useGetCmsTheme();
  const updateTheme = useUpdateCmsTheme();

  const ensurePaletteColor = useCallback(
    async (hex: string, name?: string): Promise<{ id: string; colors: CmsColor[] }> => {
      const normalized = hex.trim().toUpperCase();
      if (!isValidHex(normalized)) {
        throw new Error("Invalid hex color");
      }
      if (!theme) {
        throw new Error("CMS theme not loaded");
      }

      const existing = theme.colors.find(
        (c) => c.hex.toUpperCase() === normalized,
      );
      if (existing) {
        return { id: existing.id, colors: theme.colors };
      }

      const newName =
        name?.trim() ||
        `Couleur ${theme.colors.length + 1}`;

      const updated = await updateTheme.mutateAsync({
        ...themeToPatchPayload(theme),
        colors: [
          ...theme.colors.map((c, index) => ({
            id: c.id,
            name: c.name,
            hex: c.hex,
            sortOrder: c.sortOrder ?? index,
          })),
          {
            name: newName,
            hex: normalized,
            sortOrder: theme.colors.length,
          },
        ],
      });

      const created = updated.colors.find(
        (c) => c.hex.toUpperCase() === normalized,
      );
      if (!created) {
        throw new Error("Failed to create palette color");
      }

      return { id: created.id, colors: updated.colors };
    },
    [theme, updateTheme],
  );

  return { ensurePaletteColor, theme };
}
